// Cookie consent banner backed by Shopify's Customer Privacy API.
//
// The customerPrivacy namespace is not bundled on the storefront by default —
// it has to be requested via Shopify.loadFeatures('consent-tracking-api'). If
// the feature isn't available the banner stays hidden, since there's no
// backing API to record consent against.
//
// API surface (once loaded):
//   window.Shopify.customerPrivacy.shouldShowGDPRBanner(): boolean
//   window.Shopify.customerPrivacy.userCanBeTracked(): boolean
//   window.Shopify.customerPrivacy.setTrackingConsent(consent, callback)
//   window.Shopify.customerPrivacy.currentVisitorConsent(): { analytics, marketing, preferences, sale_of_data }

// Theme-side persistence flag. Safari's ITP can partition Shopify's own
// `_tracking_consent` cookie so that, after a successful setTrackingConsent
// write, currentVisitorConsent()/shouldShowGDPRBanner() no longer reflect the
// decision on the next load — the banner reappears even though consent WAS
// recorded. We mirror the decision into a plain first-party cookie (SameSite=Lax)
// and check it first, so the banner stays hidden regardless of how Safari treats
// Shopify's cookie.
// Suffixed with the shop so stores previewed on the same localhost never read
// each other's decision.
const CONSENT_FLAG = '_cookie_consent';

// Development switch. Set to true to keep the banner off the screen while
// working locally: behind a tracking blocker the privacy API never loads, so
// Accept cannot complete and the banner returns on every reload. Leave it
// false — it must never ship as true.
const DISABLED = false;

class CookieBanner extends HTMLElement {
  static get observedAttributes() {
    return ['data-visible'];
  }

  get #flagName() {
    const shop = this.dataset.shop;
    return shop ? `${CONSENT_FLAG}_${shop}` : CONSENT_FLAG;
  }

  #hasConsentFlag() {
    return document.cookie
      .split('; ')
      .some((c) => c.startsWith(`${this.#flagName}=1`));
  }

  #setConsentFlag() {
    // 1 year, top-level path, Secure so it survives alongside Shopify's own.
    document.cookie = `${this.#flagName}=1; max-age=31536000; path=/; SameSite=Lax; Secure`;
  }

  connectedCallback() {
    if (DISABLED) return;

    this.acceptBtn = this.querySelector('[data-cookie-action="accept"]');
    this.rejectBtn = this.querySelector('[data-cookie-action="reject"]');

    this.acceptBtn?.addEventListener('click', () => this.#setConsent(true));
    this.rejectBtn?.addEventListener('click', () => this.#setConsent(false));

    this.#init();
  }

  #init() {
    // Force-show short-circuits the privacy API entirely. It's set by the
    // "Preview in editor" toggle when the theme editor is active — there's no
    // need to wait for or even require the customerPrivacy backend to be loaded.
    if (this.hasAttribute('data-force-show')) {
      this.#show();
      return;
    }

    // Decision already recorded this/prior session — covers Safari ITP cases
    // where Shopify's own consent state doesn't read back. Skip the API entirely.
    if (this.#hasConsentFlag()) return;

    this.#loadApi().then((api) => {
      if (!api) {
        // Privacy feature couldn't be loaded — nothing to record consent
        // against, so don't show the banner.
        return;
      }
      // If the visitor already has a tracking decision recorded, stay hidden.
      // Shopify returns each consent field as an empty string before a choice
      // has been made, so treat only a truthy value as "recorded".
      if (typeof api.currentVisitorConsent === 'function') {
        const c = api.currentVisitorConsent() || {};
        if (c.analytics || c.marketing || c.preferences || c.sale_of_data) return;
      }
      if (typeof api.shouldShowGDPRBanner === 'function' && !api.shouldShowGDPRBanner()) {
        return;
      }
      this.#show();
    });
  }

  #loadApi() {
    return new Promise((resolve) => {
      const isReady = () =>
        !!(window.Shopify &&
          window.Shopify.customerPrivacy &&
          typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function');

      if (isReady()) {
        resolve(window.Shopify.customerPrivacy);
        return;
      }

      // Poll briefly in case Shopify itself isn't on window yet
      // (loadFeatures lives on the global Shopify object).
      const waitForShopify = (cb) => {
        if (window.Shopify) {
          cb();
          return;
        }
        const start = performance.now();
        const tick = () => {
          if (window.Shopify) {
            cb();
            return;
          }
          if (performance.now() - start > 3000) {
            cb();
            return;
          }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      };

      waitForShopify(() => {
        if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
          resolve(null);
          return;
        }
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => {
            if (error || !isReady()) {
              resolve(null);
              return;
            }
            resolve(window.Shopify.customerPrivacy);
          }
        );
      });
    });
  }

  #show() {
    this.hidden = false;
    this.removeAttribute('inert');
    // Trigger transition on the next frame so initial state can apply.
    requestAnimationFrame(() => this.setAttribute('data-visible', 'true'));
  }

  #hide() {
    // Move focus out before marking the element inert so screen readers and
    // browsers don't complain about focus being trapped inside hidden content.
    const active = this.ownerDocument.activeElement;
    if (active instanceof HTMLElement && this.contains(active)) {
      active.blur();
    }
    this.removeAttribute('data-visible');
    this.setAttribute('inert', '');
    const cleanup = () => {
      this.hidden = true;
      this.removeEventListener('transitionend', cleanup);
    };
    this.addEventListener('transitionend', cleanup);
    // Fallback in case transitionend doesn't fire (reduced motion, etc.)
    setTimeout(cleanup, 600);
  }

  /**
   *
   * @param {boolean} granted
   * @returns
   */
  #setConsent(granted) {
    const api = window.Shopify?.customerPrivacy;
    if (!api || typeof api.setTrackingConsent !== 'function') {
      this.#hide();
      return;
    }
    // Shopify's API expects booleans — passing 'yes'/'no' strings fails to
    // persist, so the banner would reappear on the next load.
    api.setTrackingConsent(
      {
        analytics: granted,
        marketing: granted,
        preferences: granted,
        sale_of_data: granted,
      },
      (result) => {
        // Surface the outcome: a result with `error` (e.g. "Failed to fetch")
        // means consent wasn't persisted — usually a preview/unconfigured-store
        // limitation, not a theme issue. Banner still hides for the session.
        if (result && result.error) {
          console.warn('[cookie-banner] consent not saved:', result.error);
        } else {
          // Mirror the decision into our own cookie so the banner stays hidden
          // even if Safari doesn't read Shopify's consent state back next load.
          this.#setConsentFlag();
        }
        this.#hide();
      }
    );
  }
}

if (!customElements.get('cookie-banner')) {
  customElements.define('cookie-banner', CookieBanner);
}
